import i18next from './i18n';
import type {
  NavigationIcon,
  NavigationItem,
  ParentMenuItem,
  ParentMenuItemChild,
} from './types';

export interface PlatformAppBuilderHrefParams {
  namespace: string;
  path?: string;
  clearFilter?: boolean;
}

export interface PlatformServiceNavigationItem {
  id: string;
  labelKey: string;
  href: string;
  pinnable?: boolean;
  chip?: string;
}

export interface PlatformServiceNavigationSection {
  id: string;
  labelKey: string;
  icon: NavigationIcon;
  anchorIds: string[];
  items: PlatformServiceNavigationItem[];
}

export const DATA_INTERFACE_ANCHOR_IDS = ['data interface', 'data_interface'];

const normalizeMenuKey = (value?: string | null) =>
  (value ?? '').toLowerCase().replace(/[_-]/g, ' ').trim();

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '');

export const buildPlatformAppBuilderHref = ({
  namespace,
  path = '',
  clearFilter = false,
}: PlatformAppBuilderHrefParams) => {
  const href = ['platform', 'apps', trimSlashes(namespace), trimSlashes(path)]
    .filter(Boolean)
    .join('/');

  return `/${href}${clearFilter ? '?~(clearFilter~true)' : ''}`;
};

const hasMenuItem = <T extends { id?: string }>(items: T[], id: string) =>
  items.some((item) => item.id === id);

const matchesAnchor = (anchorIds: string[], item: { id?: string }) => {
  const normalizedAnchorIds = new Set(anchorIds.map(normalizeMenuKey));
  return normalizedAnchorIds.has(normalizeMenuKey(item.id));
};

const insertAfterAnchor = <T extends { id?: string }>(
  items: T[],
  itemToInsert: T,
  anchorIds: string[],
  insertedItemIds: Set<string>,
): T[] => {
  if (!itemToInsert.id || hasMenuItem(items, itemToInsert.id)) {
    return items;
  }

  const anchorIndex = items.findIndex((item) => matchesAnchor(anchorIds, item));
  if (anchorIndex < 0) {
    return [...items, itemToInsert];
  }

  let insertionIndex = anchorIndex + 1;
  while (
    insertionIndex < items.length &&
    insertedItemIds.has(normalizeMenuKey(items[insertionIndex].id))
  ) {
    insertionIndex += 1;
  }

  return [
    ...items.slice(0, insertionIndex),
    itemToInsert,
    ...items.slice(insertionIndex),
  ];
};

const toNavigationItem = ({
  id,
  labelKey,
  href,
  chip,
  pinnable,
}: PlatformServiceNavigationItem): NavigationItem => ({
  label: i18next.t(labelKey),
  id,
  href,
  chip,
  pinnable,
});

const toParentMenuItemChild =
  (sectionId: string, parentLabel: string) =>
  ({
    id,
    labelKey,
    href,
    chip,
  }: PlatformServiceNavigationItem): ParentMenuItemChild => ({
    id,
    label: i18next.t(labelKey),
    chip,
    parentId: sectionId,
    parentLabel,
    url: href,
  });

export const createPlatformServiceNavigationItem = ({
  id,
  labelKey,
  icon,
  items,
}: PlatformServiceNavigationSection): NavigationItem => {
  const label = i18next.t(labelKey);

  return {
    label,
    id,
    icon,
    expandable: true,
    popover: {
      label,
      id: `${id}-popover`,
      items: items.map(toNavigationItem),
    },
  };
};

export const createPlatformServiceParentMenuItem = ({
  id,
  labelKey,
  items,
}: PlatformServiceNavigationSection): ParentMenuItem => {
  const label = i18next.t(labelKey);

  return {
    id,
    label,
    parentId: null,
    parentLabel: null,
    children: items.map(toParentMenuItemChild(id, label)),
  };
};

export const addPlatformServiceSectionsToNavigationItems = (
  items: NavigationItem[],
  sections: PlatformServiceNavigationSection[],
): NavigationItem[] => {
  const sectionIds = new Set(sections.map(({ id }) => normalizeMenuKey(id)));

  return sections.reduce((updatedItems, section) => {
    if (!section.items.length) {
      return updatedItems;
    }

    return insertAfterAnchor(
      updatedItems,
      createPlatformServiceNavigationItem(section),
      section.anchorIds,
      sectionIds,
    );
  }, items);
};

export const addPlatformServiceSectionsToParentMenuItems = (
  items: ParentMenuItem[],
  sections: PlatformServiceNavigationSection[],
): ParentMenuItem[] => {
  const sectionIds = new Set(sections.map(({ id }) => normalizeMenuKey(id)));

  return sections.reduce((updatedItems, section) => {
    if (!section.items.length) {
      return updatedItems;
    }

    return insertAfterAnchor(
      updatedItems,
      createPlatformServiceParentMenuItem(section),
      section.anchorIds,
      sectionIds,
    );
  }, items);
};

export const REVENUE_PLATFORM_SERVICE_SECTIONS: PlatformServiceNavigationSection[] =
  [];
